import type { ICompliancePropertyStatus } from "../../services/zeos-api/contracts/Compliance";
import type { IIncentivePropertySignals } from "../../services/zeos-api/contracts/Incentive";

export type SignalTone = "ok" | "warn" | "bad" | "unk";
export type SignalIcon = "check" | "warn" | "violation" | "unknown" | "clock" | "dot";
export type SignalGroup = "Compliance" | "Incentives";

/** Group order on the card: Incentives on top, Compliance below (task-4110). */
export const SIGNAL_GROUPS: SignalGroup[] = ["Incentives", "Compliance"];

export const COMPLIANCE_COMING_SOON_TITLE = "Compliance tracking is coming soon for this property.";

export interface ISignalChip {
  /** Set when clicking the chip should open that page for the property. */
  action?: "incentives";
  group: SignalGroup;
  icon: SignalIcon;
  key: string;
  text: string;
  title?: string;
  tone: SignalTone;
}

/** Everything a provider may read. Add a field here when a new signal needs data the card doesn't have yet. */
export interface ISignalInputs {
  compliance?: ICompliancePropertyStatus;
  incentives?: IIncentivePropertySignals;
  /** The incentive signals call hasn't settled yet; show a neutral placeholder rather than "No data". */
  incentivesLoading?: boolean;
  now?: Date;
}

/** A provider returns zero or more chips for one property. New alert types are added by appending a provider. */
export type SignalProvider = (inputs: ISignalInputs) => ISignalChip[];

/** A filing inside this many days earns its own chip (matches the "Signal semantics" table in the 4035 mockup). */
export const FILING_SOON_DAYS = 90;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const daysUntil = (iso: string | undefined, now: Date): number | null => {
  if (!iso) return null;
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return null;
  return Math.ceil((target.getTime() - now.getTime()) / MS_PER_DAY);
};

export const complianceProvider: SignalProvider = ({ compliance, now = new Date() }) => {
  // Not loaded, failed to load, not assessed, or the interim "coming soon" status: a grey placeholder so the
  // section is always there and live chips can slot in later without a layout change (task-4110).
  if (
    !compliance ||
    compliance.status === "Unknown" ||
    compliance.status === "ComingSoon"
  ) {
    return [
      {
        group: "Compliance",
        icon: "unknown",
        key: "compliance-no-data",
        text: "No data",
        title: COMPLIANCE_COMING_SOON_TITLE,
        tone: "unk",
      },
    ];
  }

  const chip = (
    key: string,
    icon: SignalIcon,
    text: string,
    tone: SignalTone,
    title?: string
  ): ISignalChip => ({ group: "Compliance", icon, key, text, title, tone });

  const chips: ISignalChip[] = [];
  const reason = compliance.statusReason;

  switch (compliance.status) {
    case "Compliant":
      chips.push(chip("compliance-status", "check", "Compliant", "ok", reason));
      break;
    case "AtRisk":
      chips.push(chip("compliance-status", "warn", "At risk", "warn", reason));
      break;
    case "ActionNeeded":
      chips.push(chip("compliance-status", "violation", "Action needed", "bad", reason));
      break;
  }

  const days = daysUntil(compliance.nextDeadline?.date, now);
  if (days !== null && days > 0 && days <= FILING_SOON_DAYS) {
    chips.push(
      chip(
        "compliance-filing",
        "clock",
        `${days}d to file`,
        "warn",
        compliance.nextDeadline?.label
      )
    );
  }
  return chips;
};

/**
 * One chip: the number of incentives identified for the property (Incentive/Signals/Portfolio). Value ($), "new",
 * "expiring" and "funds low" chips are future iterations (task-4110).
 */
export const incentiveProvider: SignalProvider = ({ incentives, incentivesLoading }) => {
  const chip = (
    key: string,
    text: string,
    tone: SignalTone,
    icon: SignalIcon,
    title?: string,
    action?: "incentives"
  ): ISignalChip[] => [{ action, group: "Incentives", icon, key, text, title, tone }];

  if (incentivesLoading && !incentives) {
    return chip("incentive-loading", "Checking…", "unk", "clock");
  }
  // Failed to load, missing from the response, or not screened (no utility data): nothing to count yet.
  if (!incentives || !incentives.isScreened) {
    return chip("incentive-no-data", "No data", "unk", "unknown", "Link a utility and meters to identify incentives.");
  }
  if (incentives.matchCount <= 0) {
    return chip("incentive-none", "No incentives identified yet", "unk", "unknown");
  }
  const n = incentives.matchCount;
  return chip(
    "incentive-matches",
    `${n} ${n === 1 ? "incentive" : "incentives"} identified`,
    "ok",
    "dot",
    "Open this property's Incentives",
    "incentives"
  );
};

export const SIGNAL_PROVIDERS: SignalProvider[] = [complianceProvider, incentiveProvider];

export const buildSignalChips = (
  inputs: ISignalInputs,
  providers: SignalProvider[] = SIGNAL_PROVIDERS
): ISignalChip[] => providers.flatMap((provider) => provider(inputs));
